/**
 * React hook wrapping the Telegram ``BackButton``.
 *
 * Shows the native back button while mounted and routes its taps to the supplied
 * handler. Hides it again on unmount (or when ``visible`` is ``false``).
 */
import { backButton } from "@telegram-apps/sdk-react";
import { useEffect } from "react";

export interface UseBackButtonOptions {
  /** Whether the back button should be visible. Defaults to ``true``. */
  readonly visible?: boolean;
}

/**
 * Wire the Telegram back button to ``onClick`` for the lifetime of the component.
 *
 * @param onClick - Invoked when the user taps the native back button.
 * @param options - Visibility control.
 */
export function useBackButton(
  onClick: () => void,
  options: UseBackButtonOptions = {},
): void {
  const visible = options.visible ?? true;

  useEffect(() => {
    if (!backButton.isMounted()) {
      return;
    }

    if (visible) {
      backButton.show();
    } else {
      backButton.hide();
    }

    const off = backButton.onClick(onClick);
    return () => {
      off();
      if (backButton.isMounted()) {
        backButton.hide();
      }
    };
  }, [onClick, visible]);
}
